import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Edit, Trash2, Users, FileText, ArrowLeft, X, Check, Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

function AdminEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', cif: '', email: '', telefono: '', direccion: '' });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Estado para importación
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importModo, setImportModo] = useState('estricto');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    cargarEmpresas();
  }, []);

  const cargarEmpresas = async () => {
    try {
      const response = await api.get('/admin/empresas');
      setEmpresas(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (empresa = null) => {
    if (empresa) {
      setEditando(empresa);
      setFormData({
        nombre: empresa.nombre || '',
        cif: empresa.cif || '',
        email: empresa.email || '',
        telefono: empresa.telefono || '',
        direccion: empresa.direccion || ''
      });
    } else {
      setEditando(null);
      setFormData({ nombre: '', cif: '', email: '', telefono: '', direccion: '' });
    }
    setError('');
    setShowModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editando) {
        await api.put(`/admin/empresas/${editando.id}`, formData);
      } else {
        await api.post('/admin/empresas', formData);
      }
      setShowModal(false);
      cargarEmpresas();
    } catch (error) {
      setError(error.response?.data?.error || 'Error guardando empresa');
    }
  };

  const eliminar = async (empresa) => {
    if (!window.confirm(`¿Eliminar empresa "${empresa.nombre}"?`)) return;

    try {
      await api.delete(`/admin/empresas/${empresa.id}`);
      cargarEmpresas();
    } catch (error) {
      alert(error.response?.data?.error || 'Error eliminando empresa');
    }
  };

  const toggleActivo = async (empresa) => {
    try {
      await api.put(`/admin/empresas/${empresa.id}`, { activo: !empresa.activo });
      cargarEmpresas();
    } catch (error) {
      alert('Error actualizando empresa');
    }
  };

  // Funciones de importación
  const descargarPlantilla = async () => {
    try {
      const response = await api.get('/admin/empresas/plantilla', { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_empresas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando plantilla:', error);
      alert('Error descargando plantilla');
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validarArchivo(file)) {
        setImportFile(file);
      }
    }
  }, []);

  const validarArchivo = (file) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    if (!validTypes.includes(file.type)) {
      alert('Tipo de archivo no válido. Use CSV o Excel (.xlsx)');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo excede el límite de 5MB');
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validarArchivo(file)) {
        setImportFile(file);
      }
    }
  };

  const importarEmpresas = async () => {
    if (!importFile) return;

    setImporting(true);
    setImportResult(null);

    const importFormData = new FormData();
    importFormData.append('archivo', importFile);
    importFormData.append('modo', importModo);

    try {
      const response = await api.post('/admin/empresas/importar', importFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data.data);

      if (response.data.data.exitosas > 0) {
        cargarEmpresas();
      }
    } catch (error) {
      setImportResult({
        error: error.response?.data?.error || 'Error importando empresas'
      });
    } finally {
      setImporting(false);
    }
  };

  const cerrarImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    setImportModo('estricto');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestión de Empresas</h1>
              <p className="text-sm text-gray-600">{empresas.length} empresas registradas</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Upload className="w-5 h-5" />
              Importar Excel
            </button>
            <button
              onClick={() => abrirModal()}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Nueva Empresa
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIF</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Usuarios</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Multas</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {empresas.map((empresa) => (
                  <tr key={empresa.id} className={!empresa.activo ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{empresa.nombre}</div>
                          {empresa.direccion && <div className="text-sm text-gray-500">{empresa.direccion}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{empresa.cif}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {empresa.email && <div>{empresa.email}</div>}
                      {empresa.telefono && <div>{empresa.telefono}</div>}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        {empresa.num_usuarios || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        <FileText className="w-4 h-4" />
                        {empresa.num_multas || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleActivo(empresa)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          empresa.activo 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {empresa.activo ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => abrirModal(empresa)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => eliminar(empresa)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">{editando ? 'Editar Empresa' : 'Nueva Empresa'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={guardar} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CIF *</label>
                  <input
                    type="text"
                    value={formData.cif}
                    onChange={(e) => setFormData({...formData, cif: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <input
                  type="text"
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editando ? 'Guardar' : 'Crear Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Importación */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Importar Empresas</h2>
              <button onClick={cerrarImportModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {!importResult ? (
              <>
                {/* Dropzone */}
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50'
                      : importFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {importFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">{importFile.name}</p>
                        <p className="text-sm text-green-600">
                          {(importFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setImportFile(null)}
                        className="ml-4 text-gray-500 hover:text-red-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">
                        Arrastra un archivo Excel o CSV aquí
                      </p>
                      <p className="text-sm text-gray-500 mb-4">o</p>
                      <label className="cursor-pointer inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                        <Upload className="w-4 h-4" />
                        Seleccionar archivo
                        <input
                          type="file"
                          className="hidden"
                          accept=".csv,.xlsx,.xls"
                          onChange={handleFileChange}
                        />
                      </label>
                    </>
                  )}
                </div>

                {/* Descargar plantilla */}
                <div className="mt-4 flex items-center justify-center">
                  <button
                    onClick={descargarPlantilla}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    <Download className="w-4 h-4" />
                    Descargar plantilla Excel
                  </button>
                </div>

                {/* Modo de importación */}
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <p className="font-medium text-gray-700 mb-3">Modo de importación:</p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="modo"
                        value="estricto"
                        checked={importModo === 'estricto'}
                        onChange={(e) => setImportModo(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-gray-700">Estricto</span>
                      <span className="text-xs text-gray-500">(Si hay errores, no importa nada)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="modo"
                        value="permisivo"
                        checked={importModo === 'permisivo'}
                        onChange={(e) => setImportModo(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-gray-700">Permisivo</span>
                      <span className="text-xs text-gray-500">(Importa las filas válidas)</span>
                    </label>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={cerrarImportModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={importarEmpresas}
                    disabled={!importFile || importing}
                    className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2 ${
                      !importFile || importing
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {importing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Importar
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Resultados de importación */
              <div>
                {importResult.error ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Error en la importación</span>
                    </div>
                    <p className="text-red-600 mt-2">{importResult.error}</p>
                  </div>
                ) : (
                  <>
                    {/* Resumen */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-gray-700">{importResult.total}</p>
                        <p className="text-sm text-gray-500">Total filas</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-green-700">{importResult.exitosas}</p>
                        <p className="text-sm text-green-600">Importadas</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-red-700">{importResult.fallidas}</p>
                        <p className="text-sm text-red-600">Fallidas</p>
                      </div>
                    </div>

                    {importResult.mensaje && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                        <p className="text-yellow-800 text-sm">{importResult.mensaje}</p>
                      </div>
                    )}

                    {/* Empresas creadas */}
                    {importResult.empresas_creadas && importResult.empresas_creadas.length > 0 && (
                      <div className="mb-4">
                        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Empresas creadas ({importResult.empresas_creadas.length})
                        </h3>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-2">Fila</th>
                                <th className="text-left px-3 py-2">Nombre</th>
                                <th className="text-left px-3 py-2">CIF</th>
                                <th className="text-center px-3 py-2">Multas vinculadas</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importResult.empresas_creadas.map((emp, idx) => (
                                <tr key={idx} className="border-t border-gray-100">
                                  <td className="px-3 py-2 text-gray-500">{emp.fila}</td>
                                  <td className="px-3 py-2">{emp.nombre}</td>
                                  <td className="px-3 py-2 font-mono text-gray-600">{emp.cif}</td>
                                  <td className="px-3 py-2 text-center">
                                    {emp.multas_vinculadas > 0 && (
                                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                                        {emp.multas_vinculadas}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Errores */}
                    {importResult.errores && importResult.errores.length > 0 && (
                      <div>
                        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          Errores ({importResult.errores.length})
                        </h3>
                        <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg bg-red-50">
                          {importResult.errores.map((err, idx) => (
                            <div key={idx} className="px-3 py-2 border-b border-red-100 last:border-b-0">
                              <span className="font-medium text-red-800">Fila {err.fila}:</span>
                              <ul className="text-sm text-red-600 mt-1">
                                {err.errores.map((e, i) => (
                                  <li key={i}>
                                    {e.campo !== 'general' && <span className="font-medium">{e.campo}: </span>}
                                    {e.mensaje}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Botón cerrar */}
                <div className="mt-6">
                  <button
                    onClick={cerrarImportModal}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEmpresas;
